import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import {
    ProductCategorySearchBuilder,
    ProductCategorySearchResponse,
    ProductSearchBuilder,
    ProductSearchResponse,
    SearchCollectionBuilder,
    Searcher,
    SearchTermPredictionBuilder,
    SearchTermPredictionResponse
} from '@relewise/client';
import { RelewiseCategoryResult, RelewiseProductResult, ProductData } from '../types/relewiseData';
import { CategoryProductsResponse, SearchSuggestions } from '../types/mapped';
import {
    NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP,
    DEFAULT_PAGE_SIZE,
    NUMBER_OF_VARIANTS_PER_PRODUCT_IN_AUTOSUGGEST,
    DISTINCT_COLOR_VARIANT_COUNT,
    RELEWISE_DISPLAYED_AT_LOCATION
} from '../const';
import { FilterOptions } from '../../../utils/productListFilters';
import { ProductListSortOption } from '../../../utils/productListSort';
import { RelewiseMappingService } from './MappingService';
import { BaseRelewiseService, InitializedRelewiseService } from './BaseRelewiseService/BaseRelewiseService';

export interface AutosuggestSearchParams {
    query: string;
    shouldFetchProducts: boolean;
    shouldFetchCategories: boolean;
    shouldFetchSearchTermPredictions: boolean;
    numberOfProductResults: number;
    numberOfCategoryResults: number;
    numberOfKeywordResults: number;
}

interface InitializedService extends InitializedRelewiseService {
    searcher: Searcher;
    relewiseMappingService: RelewiseMappingService;
}

export class RelewiseSearchService extends BaseRelewiseService<InitializedService> {
    protected searcher: Searcher | undefined;
    protected relewiseMappingService: RelewiseMappingService | undefined;

    constructor(request: IRequestContext, telemetry: ITelemetry) {
        super(request, telemetry, 'Search Service');

        if (!this.isEnabled()) {
            return;
        }

        this.relewiseMappingService = new RelewiseMappingService(request, telemetry);
        this.searcher = new Searcher(this.relewiseDatasetId, this.relewiseApiKey, {
            serverUrl: this.relewiseUrl
        });
    }

    public async autosuggestedSearch({
        query,
        shouldFetchProducts,
        shouldFetchCategories,
        shouldFetchSearchTermPredictions,
        numberOfProductResults,
        numberOfCategoryResults,
        numberOfKeywordResults
    }: AutosuggestSearchParams): Promise<SearchSuggestions | undefined> {
        const shouldFetchSomething = shouldFetchProducts || shouldFetchCategories || shouldFetchSearchTermPredictions;
        if (!this.isEnabled() || !query || !shouldFetchSomething) {
            return;
        }

        try {
            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.HEADER_SEARCH);

            const batchedRequestBuilder = new SearchCollectionBuilder();

            if (shouldFetchProducts) {
                const productBuilder = new ProductSearchBuilder(settings)
                    .pagination(p => p.setPageSize(numberOfProductResults))
                    .setExplodedVariants(NUMBER_OF_VARIANTS_PER_PRODUCT_IN_AUTOSUGGEST)
                    .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                    .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                    .filters(f => {
                        this.addProductEnvironmentFilter(f);
                        this.addProductAndVariantStockFilters(f);
                        f.addVariantDataFilter('UniqueColorId', b => b.addDistinctCondition(1), true, true);
                    });

                this.setSearchTermOrMultipleIdsFilter(productBuilder, query);
                batchedRequestBuilder.addRequest(productBuilder.build());
            }

            if (shouldFetchCategories) {
                const categoryBuilder = new ProductCategorySearchBuilder(settings)
                    .setTerm(query)
                    .pagination(p => p.setPageSize(numberOfCategoryResults))
                    .setSelectedCategoryProperties({
                        displayName: true,
                        dataKeys: [`RecordId_${this.legalEntityId}`],
                        paths: true
                    })
                    .filters(f => {
                        this.addCategoryEnvironmentFilter(f);
                        f.addProductCategoryDataFilter(
                            `ExistsInLegalEntity_${this.legalEntityId}`,
                            b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                            true,
                            true
                        );
                    });
                batchedRequestBuilder.addRequest(categoryBuilder.build());
            }

            if (shouldFetchSearchTermPredictions) {
                const searchTermPredictionBuilder = new SearchTermPredictionBuilder(settings).setTerm(query).take(numberOfKeywordResults);
                batchedRequestBuilder.addRequest(searchTermPredictionBuilder.build());
            }

            const batchResponse = await this.searcher.batch(batchedRequestBuilder.build());

            let searchTermPredictionResponse: SearchTermPredictionResponse | undefined;
            let categoryResponse: ProductCategorySearchResponse | undefined;
            let productResponse: ProductSearchResponse | undefined;
            if (shouldFetchSearchTermPredictions) {
                searchTermPredictionResponse = batchResponse?.responses?.pop() as SearchTermPredictionResponse;
            }
            if (shouldFetchCategories) {
                categoryResponse = batchResponse?.responses?.pop() as ProductCategorySearchResponse;
            }
            if (shouldFetchProducts) {
                productResponse = batchResponse?.responses?.pop() as ProductSearchResponse;
            }

            const products = (productResponse?.results ?? undefined) as RelewiseProductResult[] | undefined;
            const searchTermPredictions = searchTermPredictionResponse?.predictions ?? undefined;
            const categories = (categoryResponse?.results ?? undefined) as RelewiseCategoryResult[] | undefined;

            const mappedResult = this.relewiseMappingService.mapAutosuggestedSearchResult({
                products,
                categories,
                searchTermPredictions
            });

            return mappedResult;
        } catch (err) {
            this.logError(`autosuggestedSearch request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }

    public async getSearchProducts(
        query: string,
        itemsPerPage: number = DEFAULT_PAGE_SIZE,
        page: number = 1,
        filterOptions?: FilterOptions,
        sort: ProductListSortOption = 'recommended',
        variantsPerProduct: number = NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP
    ): Promise<CategoryProductsResponse | undefined> {
        if (!query || !this.isEnabled()) {
            return;
        }

        try {
            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.SEARCH_PLP);

            const builder = new ProductSearchBuilder(settings)
                .pagination(p => p.setPageSize(itemsPerPage).setPage(page))
                .setExplodedVariants(variantsPerProduct)
                .setSelectedProductProperties(this.getSelectedProductPropertiesForSimpleProduct())
                .setSelectedVariantProperties(this.getSelectedVariantPropertiesForSimpleProduct())
                .facets(f => {
                    f.addSalesPriceRangeFacet('Product', filterOptions?.priceRange?.min, filterOptions?.priceRange?.max);
                    f.addCategoryFacet('ImmediateParent', filterOptions?.categoryIds);
                    f.addProductDataStringValueFacet('Size', 'Variant', filterOptions?.sizes);
                    f.addProductDataStringValueFacet('ColorRefinerGroup', 'Variant', filterOptions?.colors);
                })
                .filters(f => {
                    this.addProductEnvironmentFilter(f);
                    this.addProductAndVariantStockFilters(f);
                    f.addVariantDataFilter('UniqueColorId', b => b.addDistinctCondition(DISTINCT_COLOR_VARIANT_COUNT), true, true);
                });

            this.setSearchTermOrMultipleIdsFilter(builder, query);

            if (sort === 'price-asc') {
                builder.sorting(s =>
                    s.sortByProductVariantAttribute('SalesPrice', 'Ascending', thenBy => thenBy.sortByProductRelevance('Descending'))
                );
            } else if (sort === 'price-desc') {
                builder.sorting(s =>
                    s.sortByProductVariantAttribute('SalesPrice', 'Descending', thenBy => thenBy.sortByProductRelevance('Descending'))
                );
            }

            const response = await this.searcher.searchProducts(builder.build());

            if (!response?.results) {
                return {
                    productList: [],
                    totalItems: 0,
                    currentPage: page,
                    itemsPerPage,
                    totalPages: 0
                };
            }

            const productList = response.results
                .filter(product => product.variant !== null)
                .map(product => ({
                    variant: this.relewiseMappingService?.mapRelewiseProductResult(product as RelewiseProductResult),
                    colors: this.relewiseMappingService?.mapProductColors(product.data as ProductData) || []
                }));

            const totalItems = response.hits ?? 0;
            const totalPages = itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 0;
            const redirects = (response.redirects ?? [])
                .map(redirect => ({ url: redirect.destination }))
                .filter((urlObj): urlObj is { url: string } => Boolean(urlObj.url));
            return {
                productList,
                totalItems,
                currentPage: page,
                itemsPerPage,
                totalPages,
                redirects
            };
        } catch (err) {
            this.logError(`getSearchProducts request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }

    private setSearchTermOrMultipleIdsFilter(builder: ProductSearchBuilder, query: string): void {
        if (!this.isEnabled()) {
            return;
        }

        const ids = query.split('/').filter(id => id.length > 0);
        const isMultipleIds = ids.length > 1 && ids.every(id => !isNaN(Number(id)));

        if (isMultipleIds) {
            const multipleIdsQuery = ids.map(id => this.relewiseMappingService!.prefixId(id));
            builder.filters(f => {
                f.addProductIdFilter(multipleIdsQuery);
            });
        } else {
            builder.setTerm(query);
        }
    }
}
